/**
 * Create a metric or category that exists for this organization alone.
 *
 * Offered from the Metrics and Categories steps, where the user is already
 * looking at a value from their file that the rubric has no counterpart for.
 *
 * The metric form asks for everything, and will not submit until it has it.
 * That is not form-fussiness: a custom row has no master to inherit from, so a
 * field skipped here stays empty forever, and a metric without normalization
 * rules imports and then scores nothing — which looks like success right up
 * until someone asks why it never appears.
 *
 * It starts from what the file already says (`seed`), because the file
 * repeats each metric's definition on every row. Blank defaults that disagree
 * with the file are worse than no defaults: QC then flags the file's correct
 * values as mismatches, and accepting the "fix" overwrites them.
 */
import { useMemo, useState } from "react";

import { Plus } from "lucide-react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  createDataHubCustomCategory,
  createDataHubCustomMetric,
  updateDataHubCustomMetric,
} from "../../Store/Reducers/DataHubConsoleSlice";
import {
  describeError,
  type CategoryOption,
  type CustomMetric,
} from "../data/api";
import {
  directionFrom,
  preselectCategory,
  type CustomMetricSeed,
} from "../lib/custom-metric-seed";
import { metricIdSlug } from "../lib/entity-match";

export type CreatedEntity = { id: string; name: string };

/** Values already in use, offered as suggestions. Free text is still allowed. */
export type MetricChoices = {
  units: string[];
  formats: string[];
  scales: string[];
};

type Props = {
  kind: "metric" | "category";
  /** Pre-fills the name from the unmatched value in the file. */
  initialName: string;
  /**
   * Categories this org has, for the metric form's category picker. One entry
   * per real category, by key — never labels and keys mixed, because the
   * server only accepts a key and rejects everything else.
   */
  categories: CategoryOption[];
  /** What the file says about this metric. Metric form only. */
  seed?: CustomMetricSeed;
  choices?: MetricChoices;
  /** Edit this existing custom metric instead of creating one. Metric form only. */
  editing?: CustomMetric;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (created: CreatedEntity) => void;
};

/**
 * Techniques the normalizer implements. Kept in step with
 * `metrics_service._infer_normalization_rule`; a value outside this set
 * normalizes as direct_mapping without saying so.
 */
const TECHNIQUES = [
  "direct_mapping",
  "linear_scaling",
  "percentile_ranking",
  "inverse_percentile",
  "inverse_mapping",
  "custom_scaling",
];

const inputClass =
  "h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none";
const labelClass = "text-sm font-medium text-gray-700";
// Native selects styled like the inputs, so every field in the form has its
// label above and the same height. The shared Select floats its label inside
// the border, which misaligned the rows it sat in.
const selectClass =
  "h-9 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 focus:border-purple-500 focus:outline-none";

function Field({
  id,
  label,
  hint,
  children,
  className,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1 ${className ?? ""}`}>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children}
      {hint && <p className="m-0 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

/** A text input that suggests known values but accepts anything. */
function SuggestInput({
  id,
  value,
  onChange,
  options,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  return (
    <>
      <input
        id={id}
        list={`${id}-list`}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputClass}
        autoComplete="off"
      />
      <datalist id={`${id}-list`}>
        {options.map(o => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );
}

export function AddCustomDialog({
  kind,
  initialName,
  categories,
  seed: seedProp,
  choices,
  editing,
  open,
  onOpenChange,
  onCreated,
}: Props) {
  const dispatch = useAppDispatch();
  const isMetric = kind === "metric";
  // Editing starts from the saved definition, exactly as creating starts from
  // the file.
  const seed: CustomMetricSeed | undefined = editing
    ? {
        metric_id: editing.metric_id,
        metric_name: editing.metric_name ?? undefined,
        category_id: editing.category_key ?? undefined,
        subcategory: editing.subcategory ?? undefined,
        unit_of_measure: editing.unit_of_measure ?? undefined,
        format: editing.format ?? undefined,
        scale: editing.scale ?? undefined,
        normalization_type: editing.normalization_type ?? undefined,
        normalization_direction: editing.normalization_direction ?? undefined,
        description: editing.description ?? undefined,
        business_impact: editing.business_impact ?? undefined,
        formula: editing.formula ?? undefined,
      }
    : seedProp;
  const fileDirection = directionFrom(seed?.normalization_direction);
  const fileTechnique =
    seed?.normalization_type && TECHNIQUES.indexOf(seed.normalization_type) >= 0
      ? seed.normalization_type
      : null;
  const initialCategory = useMemo(
    () => (seed ? preselectCategory(seed, categories) : null),
    // Computed once per opening; later edits are the user's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [metricId, setMetricId] = useState(seed?.metric_id ?? initialName);
  const [name, setName] = useState(
    isMetric ? (seed?.metric_name ?? initialName) : initialName
  );
  const [categoryKey, setCategoryKey] = useState(initialCategory ?? "");
  const [subcategory, setSubcategory] = useState(seed?.subcategory ?? "");
  const [uom, setUom] = useState(seed?.unit_of_measure ?? "");
  const [format, setFormat] = useState(seed?.format ?? "");
  const [scale, setScale] = useState(seed?.scale ?? "");
  const [technique, setTechnique] = useState(fileTechnique ?? "direct_mapping");
  const [direction, setDirection] = useState(fileDirection ?? "higher_better");
  const [description, setDescription] = useState(seed?.description ?? "");
  const [businessImpact, setBusinessImpact] = useState(
    seed?.business_impact ?? ""
  );
  const [formula, setFormula] = useState(seed?.formula ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanId = metricIdSlug(metricId);
  const fileSaysCategory = [seed?.category_label, seed?.category_id]
    .filter(Boolean)
    .join(" / ");

  // Mirrors CUSTOM_METRIC_REQUIRED on the server. The server is the authority —
  // this only spares the user a round trip to be told what they already know.
  const ready = isMetric
    ? Boolean(
        cleanId &&
          name.trim() &&
          categoryKey &&
          uom.trim() &&
          format.trim() &&
          scale.trim() &&
          technique &&
          direction
      )
    : Boolean(name.trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isMetric && editing) {
        await dispatch(
          updateDataHubCustomMetric({
            metricId: editing.metric_id,
            changes: {
              metric_name: name.trim(),
              category_key: categoryKey,
              unit_of_measure: uom.trim(),
              format: format.trim(),
              scale: scale.trim(),
              normalization_type: technique,
              normalization_direction: direction,
              subcategory: subcategory.trim(),
              description: description.trim(),
              business_impact: businessImpact.trim(),
              formula: formula.trim(),
            },
          })
        ).unwrap();
        onCreated({ id: editing.metric_id, name: name.trim() });
      } else if (isMetric) {
        const res = await dispatch(
          createDataHubCustomMetric({
            metric_id: cleanId,
            metric_name: name.trim(),
            category_key: categoryKey,
            unit_of_measure: uom.trim(),
            format: format.trim(),
            scale: scale.trim(),
            normalization_type: technique,
            normalization_direction: direction,
            subcategory: subcategory.trim() || undefined,
            description: description.trim() || undefined,
            business_impact: businessImpact.trim() || undefined,
            formula: formula.trim() || undefined,
            source_system: seed?.source_system || undefined,
            kpi_status: seed?.kpi_status || undefined,
          })
        ).unwrap();
        onCreated({ id: res.metric_id, name: res.metric_name ?? name.trim() });
      } else {
        const res = await dispatch(
          createDataHubCustomCategory({ label: name.trim() })
        ).unwrap();
        onCreated({ id: res.category_key, name: res.label ?? name.trim() });
      }
      onOpenChange(false);
    } catch (err: any) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CommonModal
      isOpen={open}
      toggle={() => onOpenChange(false)}
      title={
        editing
          ? "Edit custom metric"
          : `Add custom ${isMetric ? "metric" : "category"}`
      }
      subtitle={
        editing
          ? "Changes apply to future imports and scoring. The metric id cannot change because imported data is keyed on it."
          : isMetric
            ? "This metric will exist for your organization only. It has no standard definition to fall back on, so it needs a complete one. Fields are filled from your file; check them before creating."
            : "This category will exist for your organization only."
      }
      size={isMetric ? "2xl" : "lg"}
      usePortal
      footer={
        <>
          <Button variant="outline-primary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!ready || busy}
            loading={busy}
            icon={editing ? undefined : <Plus className="h-3.5 w-3.5" />}
          >
            {editing ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
        {isMetric ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              id="cx-id"
              label="Metric ID"
              hint={
                cleanId && cleanId !== metricId.trim()
                  ? `Saved as ${cleanId}`
                  : "Letters, digits and underscores."
              }
            >
              <input
                id="cx-id"
                value={metricId}
                onChange={e => setMetricId(e.target.value)}
                // Imported data is keyed on the id, so it cannot change.
                disabled={Boolean(editing)}
                className={`${inputClass} font-mono disabled:bg-gray-50 disabled:text-gray-500`}
              />
            </Field>
            <Field id="cx-name" label="Name">
              <input
                id="cx-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Renewal Velocity"
                className={inputClass}
              />
            </Field>

            <Field id="cx-cat" label="Category" className="sm:col-span-2">
              <select
                id="cx-cat"
                value={categoryKey}
                onChange={e => setCategoryKey(e.target.value)}
                className={selectClass}
              >
                <option value="">Choose a category…</option>
                {categories.map(c => (
                  <option key={c.key} value={c.key}>
                    {`${c.long_label || c.label} (${c.key})`}
                  </option>
                ))}
              </select>
              {fileSaysCategory && (
                <p
                  className={`m-0 text-[11px] ${
                    initialCategory ? "text-gray-500" : "text-amber-700"
                  }`}
                >
                  Your file says: {fileSaysCategory}
                  {initialCategory ? "" : " — no exact match, please choose."}
                </p>
              )}
            </Field>
            <Field id="cx-sub" label="Subcategory">
              <input
                id="cx-sub"
                value={subcategory}
                onChange={e => setSubcategory(e.target.value)}
                className={inputClass}
              />
            </Field>

            <Field id="cx-uom" label="Unit of measure">
              <SuggestInput
                id="cx-uom"
                value={uom}
                onChange={setUom}
                options={choices?.units ?? []}
                placeholder="%"
              />
            </Field>
            <Field id="cx-format" label="Format">
              <SuggestInput
                id="cx-format"
                value={format}
                onChange={setFormat}
                options={choices?.formats ?? []}
                placeholder="Percentage"
              />
            </Field>
            <Field id="cx-scale" label="Scale">
              <SuggestInput
                id="cx-scale"
                value={scale}
                onChange={setScale}
                options={choices?.scales ?? []}
                placeholder="0 to 100"
              />
            </Field>
            <Field id="cx-tech" label="Normalization">
              <select
                id="cx-tech"
                value={technique}
                onChange={e => setTechnique(e.target.value)}
                className={selectClass}
              >
                {TECHNIQUES.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>

            <div className="space-y-1">
              <label htmlFor="cx-dir" className={labelClass}>
                Which way is good?
              </label>
              <select
                id="cx-dir"
                value={direction}
                onChange={e => setDirection(e.target.value)}
                className={selectClass}
              >
                <option value="higher_better">Higher is better</option>
                <option value="lower_better">Lower is better</option>
              </select>
              {/* Named plainly because getting it wrong is invisible: the
                  metric still scores, just backwards. */}
              <p
                className={`m-0 mt-1 text-[11px] ${
                  fileDirection ? "text-gray-500" : "text-amber-700"
                }`}
              >
                {fileDirection
                  ? "Taken from your file. "
                  : "Your file does not say — check this. "}
                Choosing wrong scores this metric backwards, and nothing
                downstream can tell.
              </p>
            </div>

            <Field
              id="cx-desc"
              label="Description"
              hint="What this metric measures. The KPI Briefing uses it to explain the metric."
              className="sm:col-span-2"
            >
              <textarea
                id="cx-desc"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
              />
            </Field>
            <Field id="cx-impact" label="Business impact">
              <input
                id="cx-impact"
                value={businessImpact}
                onChange={e => setBusinessImpact(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field id="cx-formula" label="Formula">
              <input
                id="cx-formula"
                value={formula}
                onChange={e => setFormula(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        ) : (
          <Field id="cx-name" label="Name">
            <input
              id="cx-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Commercial"
              className={inputClass}
            />
          </Field>
        )}

        {error && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {error}
          </div>
        )}
      </div>
    </CommonModal>
  );
}

export default AddCustomDialog;
